import React, { useState, useEffect } from 'react';
import { Card, Button, Upload, message, Row, Col, Select, InputNumber, Space, Table, Tag } from 'antd';
import { PlusOutlined, ScissorOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { getVideos, uploadVideo } from '../services/api';

const { Option } = Select;

const VideoStudio = () => {
  const [videos, setVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mixConfig, setMixConfig] = useState({
    duration: 30,
    transition: 'fade',
    musicId: null
  });

  // 加载视频列表
  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const response = await getVideos({ limit: 50 });
      if (response.success) {
        setVideos(response.data.list);
      }
    } catch (error) {
      console.error('加载视频失败:', error);
    }
  };

  // 上传视频
  const uploadProps = {
    name: 'video',
    action: '/api/video/upload',
    multiple: true,
    accept: 'video/*',
    onChange(info) {
      if (info.file.status === 'uploading') {
        message.loading('上传中...');
      }
      if (info.file.status === 'done') {
        message.success(`${info.file.name} 上传成功`);
        loadVideos();
      }
      if (info.file.status === 'error') {
        message.error(`${info.file.name} 上传失败`);
      }
    }
  };

  // 选择/取消选择视频
  const toggleSelectVideo = (videoId) => {
    setSelectedVideos(prev => 
      prev.includes(videoId) 
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  // 执行混剪
  const handleMix = async () => {
    if (selectedVideos.length < 1) {
      message.warning('请至少选择一个视频素材');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/content/video/mix', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sourceVideoIds: selectedVideos,
          duration: mixConfig.duration,
          transition: mixConfig.transition
        })
      });

      const result = await response.json();

      if (result.success) {
        message.success('视频混剪成功！');
        loadVideos();
        setSelectedVideos([]);
      } else {
        message.error(result.error?.message || '混剪失败');
      }
    } catch (error) {
      message.error('混剪失败：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 视频列表表格列
  const columns = [
    {
      title: '选择',
      key: 'select',
      render: (_, record) => (
        <input
          type="checkbox"
          checked={selectedVideos.includes(record.id)}
          onChange={() => toggleSelectVideo(record.id)}
        />
      )
    },
    {
      title: '视频',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space>
          <VideoCameraOutlined />
          {text || '未命名视频'}
          {record.type === 'mixed' && <Tag color="purple">混剪</Tag>}
        </Space>
      )
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const typeMap = {
          original: '原创',
          mixed: '混剪',
          '素材': '素材'
        };
        return <Tag>{typeMap[type] || type}</Tag>;
      }
    },
    {
      title: '时长',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration) => duration ? `${duration}秒` : '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          processing: { color: 'processing', text: '处理中' },
          completed: { color: 'success', text: '完成' },
          failed: { color: 'error', text: '失败' }
        };
        const { color, text } = statusMap[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
      }
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]}>
        {/* 左侧：上传和混剪配置 */}
        <Col span={8}>
          <Card 
            title="视频上传"
            style={{ marginBottom: 16 }}
          >
            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <PlusOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持 mp4、mov、avi 等格式，单个文件不超过 100MB
              </p>
            </Upload.Dragger>
          </Card>

          <Card title="混剪配置">
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>目标时长：{mixConfig.duration}秒</div>
              <InputNumber
                min={5}
                max={60}
                value={mixConfig.duration}
                onChange={(v) => setMixConfig({ ...mixConfig, duration: v })}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>转场效果</div>
              <Select
                value={mixConfig.transition}
                onChange={(v) => setMixConfig({ ...mixConfig, transition: v })}
                style={{ width: '100%' }}
              >
                <Option value="fade">淡入淡出</Option>
                <Option value="none">无转场</Option>
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>已选素材：{selectedVideos.length} 个</div>
            </div>

            <Button 
              type="primary" 
              icon={<ScissorOutlined />} 
              onClick={handleMix}
              loading={loading}
              disabled={selectedVideos.length === 0}
              block
            >
              开始混剪
            </Button>
          </Card>
        </Col>

        {/* 右侧：视频列表 */}
        <Col span={16}>
          <Card title="视频素材库">
            <Table
              columns={columns}
              dataSource={videos}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              rowSelection={{
                selectedRowKeys: selectedVideos,
                onChange: setSelectedVideos
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default VideoStudio;
